import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ChartBar, Printer, Eye, ShieldCheck, MagnifyingGlass, CalendarCheck, Warning, FileText
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  getITVisits, getClients, getITComplianceSummary, getITComplianceAutogestion
} from '../../lib/api';
import { COMPLIANCE_STATUSES, REGULATION_REFERENCE } from '../../lib/itComplianceControls';

const percentColor = (p) => p >= 80 ? 'text-green-400' : p >= 50 ? 'text-yellow-400' : 'text-red-400';

export default function ITReportsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('visits');

  // Visits tab
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterEstado, setFilterEstado] = useState('completada');
  const [clients, setClients] = useState([]);

  // Compliance tab
  const [compliance, setCompliance] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(true);

  const loadVisits = useCallback(async () => {
    try {
      setVisitsLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterClient && filterClient !== 'all') params.client_id = filterClient;
      if (filterEstado && filterEstado !== 'all') params.estado = filterEstado;
      const res = await getITVisits(params);
      setVisits(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      toast.error('Error cargando visitas');
    } finally {
      setVisitsLoading(false);
    }
  }, [search, filterClient, filterEstado]);

  useEffect(() => { loadVisits(); }, [loadVisits]);
  useEffect(() => { getClients().then(r => setClients(r.data || [])).catch(() => {}); }, []);

  const loadCompliance = useCallback(async () => {
    try {
      setComplianceLoading(true);
      const res = await getITComplianceSummary();
      setCompliance(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      setCompliance([]);
      toast.error(err.response?.data?.detail || 'Error cargando cumplimiento');
    } finally {
      setComplianceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'compliance') loadCompliance();
  }, [activeTab, loadCompliance]);

  const handlePrintAutogestion = async (clientId, clientName) => {
    try {
      const res = await getITComplianceAutogestion(clientId);
      const html = buildAutogestionHTML(res.data, clientName);
      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (!w) return;
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando informe de autogestión');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ChartBar className="text-[#E2B05E]" size={28} /> Reportes IT
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Reportes de servicio técnico y cumplimiento de ciberseguridad por cliente.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#12141a] border border-gray-700">
            <TabsTrigger value="visits"><FileText size={14} className="mr-1" /> Reportes de Visita</TabsTrigger>
            <TabsTrigger value="compliance"><ShieldCheck size={14} className="mr-1" /> Ciberseguridad</TabsTrigger>
          </TabsList>

          {/* VISITS TAB */}
          <TabsContent value="visits" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Buscar por número, cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-[#1a1d23] border-gray-700 text-white"
                />
              </div>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="w-[200px] bg-[#1a1d23] border-gray-700 text-white">
                  <SelectValue placeholder="Cliente" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1d23] border-gray-700">
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  {clients.map(c => <SelectItem key={c._id} value={c._id}>{c.name || c.company}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger className="w-[160px] bg-[#1a1d23] border-gray-700 text-white">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1d23] border-gray-700">
                  <SelectItem value="all">Todos</SelectItem>
                  {['completada','en_sitio','programada','en_camino','pausada','cancelada'].map(s => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg overflow-hidden">
              {visitsLoading ? (
                <div className="flex justify-center h-32 items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
                </div>
              ) : visits.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CalendarCheck size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No hay visitas que coincidan</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-xs text-gray-400 uppercase">
                      <th className="text-left p-3">Visita</th>
                      <th className="text-left p-3">Fecha</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-center p-3">Activos</th>
                      <th className="text-center p-3">Estado</th>
                      <th className="text-right p-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map(v => (
                      <tr key={v._id} className="border-b border-gray-800/50 hover:bg-[#12141a]">
                        <td className="p-3 text-white font-medium">{v.visit_number}</td>
                        <td className="p-3 text-gray-300">{v.fecha_programada}{v.hora_programada && ` · ${v.hora_programada}`}</td>
                        <td className="p-3 text-gray-300">{v.client_name}</td>
                        <td className="p-3 text-center text-gray-300">{(v.assets || []).length || v.assets_count || 0}</td>
                        <td className="p-3 text-center">
                          <Badge className="bg-gray-700/30 text-gray-300 capitalize">{(v.estado || '').replace(/_/g, ' ')}</Badge>
                        </td>
                        <td className="p-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/it/visits/${v._id}`)} title="Ver detalle">
                              <Eye size={16} className="text-[#3AB8D6]" />
                            </Button>
                            <Link
                              to={`/it/visits/${v._id}/report`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-[#1E2636]"
                              title="Imprimir reporte"
                            >
                              <Printer size={16} className="text-[#E2B05E]" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance" className="space-y-4">
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="text-[#3AB8D6] flex-shrink-0" size={28} weight="duotone" />
                <div className="flex-1">
                  <h2 className="text-white font-semibold">Cumplimiento por Cliente</h2>
                  <p className="text-xs text-gray-400 mt-1">
                    {REGULATION_REFERENCE.norma} · {REGULATION_REFERENCE.emisor}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Informe de autogestión: máximo {REGULATION_REFERENCE.plazo_informe_autogestion_dias} días desde la entrada en vigencia. Plan de acción: máximo {REGULATION_REFERENCE.plazo_plan_accion_dias} días.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg overflow-hidden">
              {complianceLoading ? (
                <div className="flex justify-center h-32 items-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AB8D6]"></div>
                </div>
              ) : compliance.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShieldCheck size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Sin evaluaciones de ciberseguridad todavía</p>
                  <p className="text-xs mt-1">Llena el checklist en una visita para empezar a ver datos aquí.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-xs text-gray-400 uppercase">
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-center p-3">Cumplimiento</th>
                      <th className="text-center p-3">Cumple</th>
                      <th className="text-center p-3">Parcial</th>
                      <th className="text-center p-3">No cumple</th>
                      <th className="text-center p-3">N/A</th>
                      <th className="text-left p-3">Última evaluación</th>
                      <th className="text-right p-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compliance.map((row) => (
                      <tr key={row.client_id} className="border-b border-gray-800/50 hover:bg-[#12141a]">
                        <td className="p-3 text-white font-medium">{row.client_name}</td>
                        <td className="p-3 text-center">
                          <span className={`text-lg font-bold ${percentColor(row.percent || 0)}`}>{row.percent || 0}%</span>
                        </td>
                        <td className="p-3 text-center text-green-400">{row.counts?.cumple || 0}</td>
                        <td className="p-3 text-center text-yellow-400">{row.counts?.parcial || 0}</td>
                        <td className="p-3 text-center">
                          <span className="text-red-400 font-semibold">{row.counts?.no_cumple || 0}</span>
                          {(row.counts?.no_cumple || 0) > 0 && <Warning size={12} className="inline ml-1 text-red-400" />}
                        </td>
                        <td className="p-3 text-center text-gray-400">{row.counts?.no_aplica || 0}</td>
                        <td className="p-3 text-gray-300 text-xs">
                          {row.last_evaluation_date || '—'}
                          {row.last_visit_number && (
                            <Link to={`/it/visits/${row.last_visit_id}`} className="block text-[#3AB8D6] text-[11px] hover:underline">
                              {row.last_visit_number}
                            </Link>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrintAutogestion(row.client_id, row.client_name)}
                            className="border-[#E2B05E]/50 text-[#E2B05E] text-xs"
                          >
                            <Printer size={14} className="mr-1" /> Informe Autogestión
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="text-[11px] text-gray-500 leading-5">
              <strong className="text-gray-400">Cómo se calcula:</strong>{' '}
              "Cumple" suma 1; "Parcial" suma 0.5; "No cumple" suma 0; "No aplica" y "Sin evaluar" se excluyen del denominador.
              El porcentaje refleja la última visita por cliente con checklist llenado.
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function buildAutogestionHTML(payload, clientName) {
  // payload structure (expected from backend):
  // {
  //   client_id, client_name, evaluation_date, visit_number,
  //   responsible_name, percent, counts,
  //   items: [{ control_id, numero, titulo, descripcion, estado, evidencia, plan_accion, fecha_objetivo, notas }]
  // }
  const items = payload?.items || [];
  const fecha = payload?.evaluation_date || new Date().toISOString().slice(0, 10);
  const rowsHTML = items.map((it) => `
    <tr>
      <td>${it.numero}</td>
      <td>
        <strong>${escapeHtml(it.titulo || '')}</strong>
        <div class="muted">${escapeHtml(it.descripcion || '')}</div>
      </td>
      <td>${escapeHtml(statusLabel(it.estado))}</td>
      <td>
        ${it.evidencia ? `<div><em>Evidencia:</em> ${escapeHtml(it.evidencia)}</div>` : ''}
        ${it.plan_accion ? `<div><em>Plan:</em> ${escapeHtml(it.plan_accion)}</div>` : ''}
        ${it.fecha_objetivo ? `<div><em>Fecha objetivo:</em> ${escapeHtml(it.fecha_objetivo)}</div>` : ''}
        ${it.notas ? `<div class="muted">${escapeHtml(it.notas)}</div>` : ''}
      </td>
    </tr>
  `).join('');

  return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"/>
<title>Informe de Autogestión - ${escapeHtml(clientName || '')}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #1f2937; padding: 28px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  h2 { font-size: 13px; margin: 16px 0 6px; border-bottom: 1px solid #d1d5db; padding-bottom: 4px; }
  .muted { color: #6b7280; font-size: 11px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2937; padding-bottom: 12px; margin-bottom: 14px; }
  .meta { font-size: 11px; color: #374151; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; }
  .summary { display: flex; gap: 16px; margin: 8px 0 14px; font-size: 12px; }
  .summary div { padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; }
  .footer { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; font-size: 11px; }
  .footer .sign { border-top: 1px solid #6b7280; padding-top: 4px; text-align: center; color: #374151; }
  @page { margin: 14mm; }
</style></head><body>
<div class="head">
  <div>
    <h1>Informe de Autogestión de Cumplimiento</h1>
    <div class="muted">Controles Mínimos de Protección en Ciberseguridad</div>
    <div class="muted">Resolución No. 07 de 11 de mayo de 2026 — AIG, Panamá</div>
  </div>
  <div class="meta">
    <div><strong>Entidad:</strong> ${escapeHtml(clientName || payload?.client_name || '—')}</div>
    <div><strong>Fecha evaluación:</strong> ${escapeHtml(fecha)}</div>
    ${payload?.visit_number ? `<div><strong>Visita:</strong> ${escapeHtml(payload.visit_number)}</div>` : ''}
    ${payload?.responsible_name ? `<div><strong>Responsable:</strong> ${escapeHtml(payload.responsible_name)}</div>` : ''}
  </div>
</div>

<div class="summary">
  <div><strong>Cumplimiento:</strong> ${payload?.percent || 0}%</div>
  <div>Cumple: ${payload?.counts?.cumple || 0}</div>
  <div>Parcial: ${payload?.counts?.parcial || 0}</div>
  <div>No cumple: ${payload?.counts?.no_cumple || 0}</div>
  <div>N/A: ${payload?.counts?.no_aplica || 0}</div>
</div>

<h2>Detalle por control</h2>
<table>
  <thead><tr><th style="width:32px">#</th><th>Control</th><th style="width:90px">Estado</th><th>Evidencia / Plan de acción</th></tr></thead>
  <tbody>${rowsHTML}</tbody>
</table>

<div class="footer">
  <div class="sign">Responsable de Tecnología</div>
  <div class="sign">Administrador / Director de la Entidad</div>
</div>

<p class="muted" style="margin-top:20px">Generado ${new Date().toLocaleString()}. Plan de acción máximo 45 días calendario desde el envío de este informe.</p>
</body></html>`;
}

function statusLabel(id) {
  const meta = COMPLIANCE_STATUSES.find(s => s.id === id);
  return meta ? meta.label : 'Sin evaluar';
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
